'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCart,
  useCommerceSource,
  useBlockPart,
  tx,
  PortalLink,
  type CartLine,
  type DecimalString,
} from '@akademi/site-builder-runtime';
import { hasBuyingListsCapability } from './SavedListPicker';
import {
  AdapterPlaceholder,
  BlockRoot,
  formatDecimalMoney,
  sharedStyles,
} from '@akademi/site-builder-blocks';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * The basket: what is in it, what it costs, and the way to checkout.
 *
 * <p>Reads the shared basket through {@code useCart()} rather than fetching,
 * so an Add on a product page and this page are the same basket. Every
 * mutation replaces the whole basket with what the server returned — no total
 * is ever recomputed here, because a total the browser worked out is a total
 * that can disagree with the one the shopper is charged.
 *
 * <p>Quantities are sent as absolute values, not deltas, so a double-click
 * lands on the number the shopper chose rather than twice it.
 *
 * <p>An empty basket is not an error and not a placeholder: it renders the
 * empty state with a link back to the shop. The adapter placeholder is only for
 * a site whose host mounts no cart at all — a quote-led one, where there is
 * genuinely nothing to show.
 */
export interface CartViewProps {
  title?: Localized;
  /** Where "keep shopping" goes. */
  continueHref?: string;
  /** Where the checkout button goes. */
  checkoutHref?: string;
  showCoupon?: boolean;
  /** Shown when the basket is empty. */
  emptyMessage?: Localized;
  enableSavedLists?: boolean;
}

const MAX_QUANTITY = 9999;

export function CartView({
  title,
  continueHref = '/categories',
  checkoutHref = '/checkout',
  showCoupon = true,
  emptyMessage,
  enableSavedLists,
}: CartViewProps) {
  const cartState = useCart();
  const commerce = useCommerceSource();
  const listsSource = hasBuyingListsCapability(commerce) ? commerce.buyingLists : undefined;
  const listScope = listsSource?.getScopeKey?.();
  const showListSaving = enableSavedLists ?? Boolean(listsSource && listScope);
  const part = useBlockPart();
  const { t, i18n } = useTranslation('site-builder-blocks');
  const [coupon, setCoupon] = useState('');
  const [selectedLines, setSelectedLines] = useState<number[]>([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [listChoices, setListChoices] = useState<Array<{ id: string; name: string }>>([]);
  const [listQuery, setListQuery] = useState('');
  const [listPage, setListPage] = useState(0);
  const [listTotal, setListTotal] = useState(0);
  const [destination, setDestination] = useState('new');
  const [newName, setNewName] = useState('');
  const [listMessage, setListMessage] = useState('');
  const [listBusy, setListBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [reviewedRevision, setReviewedRevision] = useState<string>();
  const listEpoch = useRef(0), listRequest = useRef(0), listPending = useRef(false);
  useEffect(() => { listEpoch.current++; listRequest.current++; listPending.current = false; setSelectedLines([]); setSaveOpen(false); setListChoices([]); setListQuery(''); setListPage(0); setListTotal(0); setDestination('new'); setNewName(''); setListMessage(''); setConflict(false); setReviewedRevision(undefined); }, [listsSource, listScope]);

  if (!cartState || !cartState.available) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="CartView"
            adapterName="CommerceSource"
            reason={t('commerce.cart.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  const { cart, busy, error } = cartState;
  const locale = i18n.language || 'en';
  const lines = cart?.lines ?? [];
  const ordinary = (line: CartLine) => line.parentLineId == null && (line.lineType == null || line.lineType === 'PRODUCT')
    && typeof line.productId === 'number' && Number.isSafeInteger(line.productId) && line.productId > 0
    && /^(?:0\.[0-9]{1,3}|[1-9][0-9]{0,3}(?:\.[0-9]{1,3})?)$/.test(String(line.quantity));
  const selected = lines.filter((line) => selectedLines.includes(line.id) && ordinary(line));
  const loadListChoices = async (query: string, page: number) => { if (!listsSource) return; const current = listEpoch.current, request = ++listRequest.current; setListBusy(true); setListMessage('');
    try { const result = await listsSource.list({ q: query.trim() || undefined, page, size: 50 }); if (current === listEpoch.current && request === listRequest.current) { setListChoices(result.items); setListPage(page); setListTotal(result.total ?? result.items.length); } }
    catch { if (current === listEpoch.current && request === listRequest.current) setListMessage('Saved lists could not be loaded. Retry after signing in if needed.'); }
    finally { if (current === listEpoch.current && request === listRequest.current) setListBusy(false); } };
  const openSave = () => { setSaveOpen(true); void loadListChoices('',0); };
  const saveSelected = async () => { if (!listsSource || listPending.current || selected.length === 0 || selected.length > 500 || conflict) return;
    if (destination === 'new' && (!newName.trim() || newName.trim().length > 200)) { setListMessage('Enter a list name of 1 to 200 characters.'); return; }
    const current = listEpoch.current; const items = selected.map((line) => ({ productId: String(line.productId), quantity: String(line.quantity) }));
    listPending.current = true; setListBusy(true); setListMessage('');
    try { const result = destination === 'new' ? await listsSource.create({ name: newName.trim(), items })
      : await (async () => { const revision = reviewedRevision ?? (await listsSource.detail(destination)).revision;
        if (current !== listEpoch.current) return undefined;
        return listsSource.addItems(destination,revision,items); })();
      if (current !== listEpoch.current || !result) return;
      setListMessage(`Saved ${selected.length} product line(s) to ${result.name}. The basket is unchanged.`);
      setSelectedLines([]); setReviewedRevision(undefined);
    } catch (cause) { if (current !== listEpoch.current) return;
      const status = cause && typeof cause === 'object' && 'status' in cause ? cause.status : undefined;
      if (status === 409) { setConflict(true); setReviewedRevision(undefined); setListMessage('The list changed. Review it before retrying; your selection is retained.'); }
      else setListMessage(status === 401 ? 'Sign in, then retry this selection.' : 'The selection was not saved. Review before retrying.'); }
    finally { listPending.current = false; if (current === listEpoch.current) setListBusy(false); } };
  const reviewList = async () => { if (!listsSource || destination === 'new') return; const current = listEpoch.current;
    try { const detail = await listsSource.detail(destination); if (current === listEpoch.current) { setReviewedRevision(detail.revision); setConflict(false); setListMessage(`Review ${detail.name} at revision ${detail.revision}, then retry.`); } }
    catch { if (current === listEpoch.current) setListMessage('The list could not be reviewed.'); } };
  const money = (amount: DecimalString | undefined) =>
    formatDecimalMoney(amount, cart?.currency ?? '', locale);

  return (
    <BlockRoot as="section">
      <style dangerouslySetInnerHTML={{ __html: CART_CSS }} />
      <div style={{ ...sectionInner, maxWidth: 1180, padding: 'clamp(32px, 6vw, 72px) 24px' }}>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.cart.title')}
        </h1>

        {error && (
          <p {...part('error')} role="alert" style={errorStyle}>
            {error}
          </p>
        )}

        {lines.length === 0 ? (
          <div {...part('empty')} style={emptyStyle}>
            <p style={{ margin: '0 0 16px' }}>
              {tx(emptyMessage) || t('commerce.cart.empty')}
            </p>
            <PortalLink href={continueHref} style={secondaryButtonStyle}>
              {t('commerce.cart.continue')}
            </PortalLink>
          </div>
        ) : (
          <div className="commerce-cart-layout">
            <div>
              <ul {...part('lines')} style={listStyle}>
                {lines.map((line) => (
                  <LineRow
                    key={line.id}
                    line={line}
                    busy={busy}
                    money={money}
                    onQuantity={(q) => void cartState.setQuantity(line.id, q)}
                    onRemove={() => void cartState.remove(line.id)}
                    removeLabel={t('commerce.cart.remove')}
                    quantityLabel={t('commerce.cart.quantity')}
                    selectable={showListSaving && Boolean(listsSource && listScope) && ordinary(line)}
                    selected={selectedLines.includes(line.id)}
                    onSelect={(checked) => setSelectedLines((current) => checked ? [...current, line.id] : current.filter((id) => id !== line.id))}
                  />
                ))}
              </ul>
              {showListSaving && listsSource && listScope && <section aria-label="Save selected basket products" style={{ margin: '16px 0', padding: 16, border: '1px solid var(--color-border-subtle, #ddd)', borderRadius: 8 }}>
                <h2>Save selected products to a list</h2>
                {lines.some((line) => !ordinary(line)) && <p>Configured, child, quote, or otherwise unsupported basket lines are not ordinary products and cannot be saved as product rows.</p>}
                <button type="button" disabled={selected.length === 0 || listBusy} onClick={openSave}>Choose saved list</button>
                {saveOpen && <div><label>Find list<input type="search" value={listQuery} maxLength={100} onChange={(event) => { listRequest.current++; setListQuery(event.target.value); setListChoices([]); setListPage(0); setListTotal(0); }} /></label>
                  <button type="button" disabled={listBusy} onClick={() => void loadListChoices(listQuery,0)}>Search lists</button>
                  <label>Destination<select value={destination} onChange={(event) => { setDestination(event.target.value); setConflict(false); setReviewedRevision(undefined); }}><option value="new">Create new list</option>{listChoices.map((choice) => <option key={choice.id} value={choice.id}>{choice.name}</option>)}</select></label>
                  {listTotal > 50 && <div><button type="button" disabled={listPage === 0 || listBusy} onClick={() => void loadListChoices(listQuery,listPage-1)}>Previous lists</button>
                    <span>Page {listPage+1} of {Math.ceil(listTotal/50)}</span>
                    <button type="button" disabled={(listPage+1)*50 >= listTotal || listBusy} onClick={() => void loadListChoices(listQuery,listPage+1)}>Next lists</button></div>}
                  {destination === 'new' && <label>New list name<input value={newName} onChange={(event) => setNewName(event.target.value)} maxLength={200} /></label>}
                  {listMessage && <p role="status">{listMessage}</p>}
                  {conflict && <button type="button" onClick={() => void reviewList()}>Review latest list</button>}
                  <button type="button" disabled={listBusy || conflict || selected.length === 0} onClick={() => void saveSelected()}>{listBusy ? 'Saving…' : 'Save selection'}</button>
                  <button type="button" disabled={listBusy} onClick={() => setSaveOpen(false)}>Cancel</button>
                </div>}
              </section>}
              <PortalLink href={continueHref} style={continueLinkStyle}>
                ← {t('commerce.cart.continue')}
              </PortalLink>
            </div>

            <aside className="commerce-cart-summary" aria-label={t('commerce.cart.total')}>
              <h2 style={summaryTitleStyle}>{t('commerce.cart.title')}</h2>
              {showCoupon && (
                <form
                  {...part('coupon')}
                  style={couponStyle}
                  onSubmit={(event) => {
                    event.preventDefault();
                    const code = coupon.trim();
                    if (code) void cartState.applyCoupon(code);
                  }}
                >
                  <label htmlFor="cart-coupon" style={srOnly}>
                    {t('commerce.cart.couponLabel')}
                  </label>
                  <input
                    id="cart-coupon"
                    value={coupon}
                    onChange={(event) => setCoupon(event.target.value)}
                    placeholder={t('commerce.cart.couponPlaceholder')}
                    style={inputStyle}
                  />
                  <button type="submit" disabled={busy || !coupon.trim()} style={secondaryButtonStyle}>
                    {t('commerce.cart.applyCoupon')}
                  </button>
                </form>
              )}

              <dl {...part('totals')} style={totalsStyle}>
                <Total label={t('commerce.cart.subtotal')} value={money(cart?.subtotal)} />
                {cart?.discountTotal && cart.discountTotal !== '0.00' && (
                  <Total label={t('commerce.cart.discount')} value={`-${money(cart.discountTotal)}`} />
                )}
                <Total
                  label={t('commerce.cart.shipping')}
                  value={
                    cart && cart.shippingTotal !== '0.00'
                      ? money(cart.shippingTotal)
                      : t('commerce.cart.shippingAtCheckout')
                  }
                />
                <Total label={t('commerce.cart.tax')} value={money(cart?.taxTotal)} />
                <Total label={t('commerce.cart.total')} value={money(cart?.total)} emphasis />
              </dl>

              <div {...part('actions')} style={actionsStyle}>
                <PortalLink href={checkoutHref} style={primaryButtonStyle}>
                  {t('commerce.cart.checkout')} →
                </PortalLink>
              </div>
            </aside>
          </div>
        )}
      </div>
    </BlockRoot>
  );
}

function LineRow({
  line,
  busy,
  money,
  onQuantity,
  onRemove,
  removeLabel,
  quantityLabel,
  selectable,
  selected,
  onSelect,
}: {
  line: CartLine;
  busy: boolean;
  money: (amount: DecimalString | undefined) => string;
  onQuantity: (quantity: DecimalString) => void;
  onRemove: () => void;
  removeLabel: string;
  quantityLabel: string;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (checked: boolean) => void;
}) {
  return (
    <li style={lineStyle}>
      {selectable && <label><input type="checkbox" checked={Boolean(selected)} onChange={(event) => onSelect?.(event.target.checked)} />Save {line.description} to list</label>}
      <div>
        <p style={descriptionStyle}>{line.description}</p>
        <p style={unitStyle}>{money(line.unitPrice)}</p>
        {line.availabilityNote && <p style={noteStyle}>{line.availabilityNote}</p>}
      </div>
      {/* A second row, not a continuation of the same flex line: with the title above it in
          its own full-width block, quantity/price/remove no longer compete with it for space
          on one line — which on a narrow card previously left the title only the leftover
          width after those three fixed-size controls, wrapping it one word per line. */}
      <div style={lineControlsStyle}>
        <label style={srOnly} htmlFor={`cart-qty-${line.id}`}>
          {quantityLabel}
        </label>
        <input
          id={`cart-qty-${line.id}`}
          type="number"
          min={1}
          max={MAX_QUANTITY}
          step="any"
          defaultValue={line.quantity}
          disabled={busy}
          style={quantityStyle}
          onBlur={(event) => {
            const next = event.target.value.trim();
            if (next && next !== line.quantity) onQuantity(next);
          }}
        />
        <div style={lineEndStyle}>
          <span style={lineTotalStyle}>{money(line.lineTotal)}</span>
          <button type="button" onClick={onRemove} disabled={busy} style={removeStyle}>
            {removeLabel}
          </button>
        </div>
      </div>
    </li>
  );
}

function Total({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div style={totalRowStyle}>
      <dt style={{ margin: 0, fontWeight: emphasis ? 600 : 400 }}>{label}</dt>
      <dd style={{ margin: 0, fontWeight: emphasis ? 700 : 400 }}>{value}</dd>
    </div>
  );
}

const titleStyle: CSSProperties = { margin: '0 0 clamp(24px, 4vw, 44px)', fontFamily: 'var(--font-family-display, inherit)', fontSize: 'clamp(34px, 5vw, 56px)', lineHeight: 1.05, fontWeight: 650 };
const errorStyle: CSSProperties = {
  margin: '0 0 16px',
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-danger-50, #fef2f2)',
  color: 'var(--color-danger-700, #b91c1c)',
};
const emptyStyle: CSSProperties = { padding: '32px 0' };
const listStyle: CSSProperties = { display: 'grid', gap: 12, listStyle: 'none', margin: 0, padding: 0 };
const lineStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '20px',
  border: '1px solid var(--color-border-subtle, #e5e7eb)',
  borderRadius: 'var(--radius-card, 10px)',
  background: 'var(--color-surface-card, #fff)',
};
const lineControlsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexWrap: 'wrap',
};
const lineEndStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 16, marginLeft: 'auto' };
const descriptionStyle: CSSProperties = { margin: 0, fontWeight: 600 };
const unitStyle: CSSProperties = {
  margin: '4px 0 0',
  color: 'var(--color-text-secondary, #6b7280)',
  fontSize: 14,
};
const noteStyle: CSSProperties = {
  margin: '4px 0 0',
  color: 'var(--color-warning-700, #b45309)',
  fontSize: 13,
};
const quantityStyle: CSSProperties = { width: 80, padding: '8px 10px', borderRadius: 'var(--radius-sm, 6px)', border: '1px solid var(--color-border, #e5e7eb)' };
const lineTotalStyle: CSSProperties = { minWidth: 90, textAlign: 'right', fontWeight: 600 };
const removeStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--color-text-secondary, #6b7280)',
  cursor: 'pointer',
  textDecoration: 'underline',
};
const couponStyle: CSSProperties = { display: 'flex', gap: 8, margin: '0 0 24px', flexWrap: 'wrap' };
const inputStyle: CSSProperties = {
  flex: '1 1 200px',
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm, 6px)',
  border: '1px solid var(--color-border, #e5e7eb)',
};
const totalsStyle: CSSProperties = { margin: '0 0 24px', display: 'grid', gap: 12 };
const totalRowStyle: CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 16 };
const actionsStyle: CSSProperties = { display: 'grid' };
const primaryButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 50,
  padding: '12px 24px',
  borderRadius: 'var(--radius-button, 8px)',
  background: 'var(--color-brand-600, #2563eb)',
  color: 'var(--color-text-on-brand, #fff)',
  textDecoration: 'none',
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
};
const secondaryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-surface-muted, #f3f4f6)',
  color: 'var(--color-text, #111827)',
  textDecoration: 'none',
  border: '1px solid var(--color-border, #e5e7eb)',
  cursor: 'pointer',
};
const summaryTitleStyle: CSSProperties = { margin: '0 0 20px', fontFamily: 'var(--font-family-display, inherit)', fontSize: 22, fontWeight: 650 };
const continueLinkStyle: CSSProperties = { display: 'inline-flex', marginTop: 24, color: 'var(--color-text-primary, #111827)', fontSize: 14, fontWeight: 650, textDecoration: 'none' };
const CART_CSS = `
.commerce-cart-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 380px);
  gap: clamp(2rem, 5vw, 5rem);
  align-items: start;
}
.commerce-cart-summary {
  position: sticky;
  top: 96px;
  padding: clamp(20px, 3vw, 30px);
  border: 1px solid var(--color-border-subtle, #e5e7eb);
  border-radius: var(--radius-card, 10px);
  background: var(--color-surface-card, #fff);
  box-shadow: var(--shadow-md, 0 8px 24px rgba(0,0,0,.08));
}
@media (max-width: 820px) {
  .commerce-cart-layout { grid-template-columns: 1fr; }
  .commerce-cart-summary { position: static; }
}
`;
const srOnly: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};
